import { makeAutoObservable } from 'mobx';
import ConfirmManager from 'src/libs/ui-kit/confirm-dialog/ConfirmManager';
import { VisibilityStore } from 'src/modules/users/stores/VisibilityStore';
import ToastifyService from 'src/services/ToastifyService';
import { IUserContactRowTableProps } from '../components/tables/UserContactTableRow';
import { CONTACT_TYPE_STRING, UserContactFormType } from '../constants';
import { IUserContactsServiceProvider } from '../service-provider';
import { CONTACT_TYPE, IUserContact, IUserContactResponse } from '../service-provider/interface';

export class UserContactsWidgetStore {
  hasError = false;

  private contactsAreLoading = false;
  contacts: IUserContactResponse | undefined;

  createModal = new VisibilityStore();
  isCreatingContact = false;

  awaitingDeletingContacts = new Set<string>();

  // eslint-disable-next-line max-params
  constructor(readonly userId: string, readonly userContactsService: IUserContactsServiceProvider) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get getIsContactsInitialLoading(): boolean {
    // Если контакты уже есть и делается повторная загрузка, значит это silent refresh
    return this.contactsAreLoading && !this.contacts;
  }

  get getArePhonesVisible(): boolean {
    return !!this.contacts?.phone?.length;
  }

  get getAreEmailsVisible(): boolean {
    return !!this.contacts?.email?.length;
  }

  get getPhones(): IUserContactRowTableProps[] {
    return (this.contacts?.phone ?? []).map(this.getContactRowProps);
  }

  get getEmails(): IUserContactRowTableProps[] {
    return (this.contacts?.email ?? []).map(this.getContactRowProps);
  }

  private getContactRowProps(contact: IUserContact): IUserContactRowTableProps {
    return {
      componentKey: contact.id!,
      isMain: !!contact.isMain,
      isVerified: !!contact.isVerified,
      value: contact.value ?? '',
      isDeleted: !!contact.isDeleted,
      isAwaitingDeleteRestore: this.awaitingDeletingContacts.has(contact.id!),
      onChange: () => this.setContactMain(contact.id!),
      onDeleteClick: () => this.deleteContact(contact.id!),
    };
  }

  async loadContactsByContractorId(): Promise<void> {
    this.contactsAreLoading = true;
    this.hasError = false;

    try {
      this.contacts = await this.userContactsService.searchContacts({ id: this.userId });
    } catch {
      this.contacts = undefined;
      this.hasError = true;
    } finally {
      this.contactsAreLoading = false;
    }
  }

  async createContact(contact: UserContactFormType) {
    this.isCreatingContact = true;

    try {
      const value = contact.type === CONTACT_TYPE.MOBILE ? contact.phoneValue : contact.emailValue;

      this.contacts = await this.userContactsService.createContact({
        userId: this.userId,
        contact: {
          type: contact.type,
          value,
        },
      });

      this.createModal.hide();
      const label = CONTACT_TYPE_STRING[contact.type];

      ToastifyService.toast(`Контакт успешно создан: ${value}, тип: ${label}`, {
        type: 'success',
      });
    } finally {
      this.isCreatingContact = false;
    }
  }

  async deleteContact(contactId: string | undefined): Promise<void> {
    return ConfirmManager.show({
      message: 'Вы уверены, что хотите удалить контакт?',
      onConfirm: () => this.handleDeleteContactConfirmation(contactId),
    }).then(() => {
      ConfirmManager.hide();
    });
  }

  async setContactMain(contactId: string) {
    this.contacts = await this.userContactsService.setContactMain({
      userId: this.userId,
      contactId,
    });
  }

  private async handleDeleteContactConfirmation(contactId: string | undefined): Promise<void> {
    this.awaitingDeletingContacts.add(contactId!);

    try {
      this.contacts = await this.userContactsService.deleteContact({ userId: this.userId, contactId });
    } finally {
      this.awaitingDeletingContacts.delete(contactId!);
    }
  }
}
